/**
 * Kleines Saeulendiagramm fuer Zaehlwerte (F-02), z. B. erledigte Aufgaben je
 * Woche. Eine Reihe, eine Farbe (der Akzent), schmale Saeulen mit Luft
 * dazwischen, oben abgerundet und auf einer gemeinsamen Grundlinie.
 *
 * Beschriftet sind nur die juengste und die hoechste Saeule; jeder Wert steht
 * im Tooltip und als Liste fuer Screenreader - nichts haengt nur am Hover.
 * Zeitraeume ohne Daten (`null`) sind neutral markiert, nicht als 0 (V-16).
 */
export interface SaeulenPunkt {
  key: string;
  /** Voller Name fuer Tooltip und Screenreader, z. B. "KW 38 (14.09.–20.09.)". */
  titel: string;
  /** `null` = keine Daten in diesem Zeitraum. */
  wert: number | null;
}

export function Saeulen({ label, punkte, einheit, links, rechts, format = String }: {
  label: string;
  punkte: SaeulenPunkt[];
  /** Hinter dem Wert im Tooltip, z. B. "erledigt". */
  einheit: string;
  /** Achsenbeschriftung unter der ersten und der letzten Saeule. */
  links: string;
  rechts: string;
  /** Wie ein Wert als Text erscheint, z. B. "2,5" statt "2.5". */
  format?: (wert: number) => string;
}) {
  const max = Math.max(0, ...punkte.map((p) => p.wert ?? 0));
  const iMax = punkte.findIndex((p) => p.wert === max);
  const beschriftet = (i: number) => (i === punkte.length - 1 || (i === iMax && max > 0));

  return (
    <figure className="flex flex-col gap-1">
      <figcaption className="text-[11px] text-ink-muted">{label}</figcaption>
      <ol aria-label={label} className="flex h-24 items-end gap-0.5 border-b border-line pt-4">
        {punkte.map((p, i) => {
          if (p.wert === null) {
            return (
              <li key={p.key} title={`${p.titel}: keine Daten`} aria-label={`${p.titel}: keine Daten`} className="flex h-full min-w-0 flex-1 items-end justify-center">
                <span className="block h-1 w-full max-w-6 rounded-sm bg-line/35" />
              </li>
            );
          }
          const wert = p.wert;
          const text = `${p.titel}: ${format(wert)} ${einheit}`;
          const hoehe = max === 0 ? 0 : (wert / max) * 100;
          return (
            <li key={p.key} title={text} aria-label={text} className="group relative flex h-full min-w-0 flex-1 items-end justify-center">
              {wert > 0 && (
                <span
                  className="block w-full max-w-6 rounded-t-[4px] bg-accent transition-[filter] group-hover:brightness-125"
                  style={{ height: `${Math.max(4, hoehe)}%` }}
                />
              )}
              {beschriftet(i) && (
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 -translate-x-1/2 text-[10px] tabular-nums text-ink-muted"
                  style={{ bottom: `calc(${Math.max(wert > 0 ? 4 : 0, hoehe)}% + 2px)` }}
                >
                  {format(wert)}
                </span>
              )}
            </li>
          );
        })}
      </ol>
      <div className="flex justify-between text-[10px] tabular-nums text-ink-faint">
        <span>{links}</span>
        <span>{rechts}</span>
      </div>
    </figure>
  );
}
